import { useParams } from 'react-router';
import theme from './ElectricitySupplier.module.scss';
import {
    createElectricitySupplierOffer,
    getElectricitySupplierOffer,
    updateElectricitySupplierOffer,
    updateLogo,
} from 'services/serviceProvider';
import { useMergeState } from 'utils/hooks';
import React, { useEffect, useState } from 'react';
import {
    IconToast,
    LoadingIndicator,
    ValidatingInput,
    ValidationForm,
    ColorPicker,
    ImageUpload,
    Checkbox,
    ValidatingTextArea,
    Button,
    FileData,
    Thumbnail,
} from '@ygtechnologies/bygr-frontend-component-library';
import { ElectricitySupplier, ElectricitySupplierOffer } from 'types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-light-svg-icons';

interface ElectricitySupplierDetailsProps {
    id: number;
}

const ElectricitySupplierDetails: React.FC<ElectricitySupplierDetailsProps> = (props) => {
    const [isSaving, setIsSaving] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const { offerId } = useParams<{ offerId: string }>();
    const [offer, mergeData, setData] = useMergeState<ElectricitySupplierOffer>({
        id: 0,
        title: '',
        logoId: 0,
        logoFile: null,
        color: '',
        supplier: ElectricitySupplier.Fjordkraft,
        description: '',
        externalId: '',
        isCompanyOffer: false,
        termSheetLink1: '',
        termSheetLink2: '',
        termSheetLink3: '',
        terms: '',
        priceListLink: '',
        sortOrder: 1,
        highlightedTitle1: '',
        highlightedSubTitle1: '',
        highlightedTitle2: '',
        highlightedSubTitle2: '',
        highlightedTitle3: '',
        highlightedSubTitle3: '',
        protocolAdditionalInfo: '',
    });

    useEffect(() => {
        const loadData = async () => {
            if (offerId && +offerId !== 0) {
                setData(await getElectricitySupplierOffer(+offerId));
            }
        };

        loadData();
    }, [offerId, setData]);

    useEffect(() => {
        const loadLogo = async () => {
            if (offer.logoId !== 0) {
                const logo = await FileData.download(`api/serviceprovider/logo/${offer.logoId}`);
                mergeData({ logoFile: logo });
            }
        };

        loadLogo();
    }, [offer.logoId, mergeData]);

    const handleSubmit = async (e) => {
        if (!offer) return;

        setIsSaving(true);

        try {
            let result: Partial<ElectricitySupplierOffer>;
            if (offer.id === 0) {
                result = await createElectricitySupplierOffer(offer);
            } else {
                result = await updateElectricitySupplierOffer(offer);
            }

            mergeData(result);
            IconToast.success('Saved');
        } catch (e: any) {
            IconToast.error(e.message);
        } finally {
            setIsSaving(false);
        }
    };

    if (!offer) {
        return <LoadingIndicator />;
    }

    return (
        <div>
            <ValidationForm className={theme.body} onSubmit={handleSubmit}>
                <div className={theme.row}>
                    <div className={theme.pagePadding}>
                        <h4>Offer information</h4>
                        <label className={theme.inputs}>
                            Title
                            <span className={theme.required}>*</span>
                            <ValidatingInput
                                type="text"
                                required
                                className="form-control"
                                placeholder="Offer title"
                                value={offer.title}
                                onChange={(e) => mergeData({ title: e.target.value })}
                            />
                        </label>

                        <label className={theme.inputs}>
                            Logo
                            <ImageUpload
                                shouldCrop={false}
                                containerClassName={theme.imageUpload}
                                aspectRatio={1}
                                currentImage={offer.logoFile}
                                onChange={(image) => mergeData({ logoFile: image })}
                            />
                        </label>

                        <label className={theme.inputs}>
                            Color
                            <span className={theme.required}>*</span>
                            <ColorPicker
                                value={offer.color}
                                className={theme.colorPicker}
                                required={true}
                                placeholder="Color"
                                onChange={(e) => mergeData({ color: e })}
                            />
                        </label>

                        <label className={theme.inputs}>
                            Supplier
                            <span className={theme.required}>*</span>
                            <select
                                name="suppliers"
                                className="form-control"
                                value={offer.supplier}
                                onChange={(e) => mergeData({ supplier: e.target.value as ElectricitySupplier })}>
                                {Object.keys(ElectricitySupplier).map((key) => (
                                    <option key={key} value={key}>
                                        {key}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className={theme.inputs}>
                            Product Id
                            <span className={theme.required}>*</span>
                            <ValidatingInput
                                type="text"
                                required
                                className="form-control"
                                placeholder="Product ID from supplier"
                                value={offer.externalId}
                                onChange={(e) => mergeData({ externalId: e.target.value })}
                            />
                        </label>

                        <label className={theme.inputs}>
                            Description
                            <ValidatingTextArea
                                className="form-control"
                                placeholder="Description"
                                value={offer.description}
                                onChange={(e) => mergeData({ description: e.target.value })}
                            />
                        </label>

                        <Checkbox
                            checked={offer.isCompanyOffer}
                            className={theme.inputs}
                            onChange={(e) => mergeData({ isCompanyOffer: e.target.checked })}>
                            Is Company Offer
                        </Checkbox>
                    </div>

                    <div className={theme.pagePadding}>
                        <h4>Highlighted info</h4>
                        <label className={theme.inputs}>
                            Name of offer
                            <ValidatingInput
                                type="text"
                                className="form-control"
                                placeholder="Highlighted Title 1"
                                value={offer.highlightedTitle1}
                                onChange={(e) => mergeData({ highlightedTitle1: e.target.value })}
                            />
                        </label>

                        <label className={theme.inputs}>
                            Name of supplier (electricity company)
                            <ValidatingInput
                                type="text"
                                className="form-control"
                                placeholder="Highlighted Sub Title 1"
                                value={offer.highlightedSubTitle1}
                                onChange={(e) => mergeData({ highlightedSubTitle1: e.target.value })}
                            />
                        </label>

                        <label className={theme.inputs}>
                            How much per kWh
                            <ValidatingInput
                                type="text"
                                className="form-control"
                                placeholder="Highlighted Title 2"
                                value={offer.highlightedTitle2}
                                onChange={(e) => mergeData({ highlightedTitle2: e.target.value })}
                            />
                        </label>

                        <label className={theme.inputs}>
                            Monthly amount
                            <ValidatingInput
                                type="text"
                                className="form-control"
                                placeholder="Hihglighted Sub Title 2"
                                value={offer.highlightedSubTitle2}
                                onChange={(e) => mergeData({ highlightedSubTitle2: e.target.value })}
                            />
                        </label>

                        <label className={theme.inputs}>
                            Binding period
                            <ValidatingInput
                                type="text"
                                className="form-control"
                                placeholder="Highlighted Title 3"
                                value={offer.highlightedTitle3}
                                onChange={(e) => mergeData({ highlightedTitle3: e.target.value })}
                            />
                        </label>

                        <label className={theme.inputs}>
                            Break fee and advance payment
                            <ValidatingInput
                                type="text"
                                className="form-control"
                                placeholder="Highlighted Sub Title 3"
                                value={offer.highlightedSubTitle3}
                                onChange={(e) => mergeData({ highlightedSubTitle3: e.target.value })}
                            />
                        </label>
                    </div>
                </div>

                <div className={theme.row}>
                    <div className={theme.pagePadding}>
                        <h4>Terms links</h4>
                        <label className={theme.inputs}>
                            Terms and Conditions Link
                            <ValidatingInput
                                type="text"
                                className="form-control"
                                placeholder="Term Sheet Link 1"
                                value={offer.termSheetLink1}
                                onChange={(e) => mergeData({ termSheetLink1: e.target.value })}
                            />
                        </label>

                        <label className={theme.inputs}>
                            Right of Withdrawal Link
                            <ValidatingInput
                                type="text"
                                className="form-control"
                                placeholder="Term Sheet Link 2"
                                value={offer.termSheetLink2}
                                onChange={(e) => mergeData({ termSheetLink2: e.target.value })}
                            />
                        </label>

                        <label className={theme.inputs}>
                            Information on RoW Link
                            <ValidatingInput
                                type="text"
                                className="form-control"
                                placeholder="Term Sheet Link 3"
                                value={offer.termSheetLink3}
                                onChange={(e) => mergeData({ termSheetLink3: e.target.value })}
                            />
                        </label>

                        <label className={theme.inputs}>
                            Price List Link
                            <ValidatingInput
                                type="text"
                                className="form-control"
                                placeholder="Price List Link"
                                value={offer.priceListLink}
                                onChange={(e) => mergeData({ priceListLink: e.target.value })}
                            />
                        </label>

                        <label className={theme.inputs}>
                            Confirmation
                            <ValidatingTextArea
                                className="form-control"
                                placeholder="Terms"
                                value={offer.terms}
                                onChange={(e) => mergeData({ terms: e.target.value })}
                            />
                        </label>

                        <label className={theme.inputs}>
                            Additional protocol information (shown in protocol)
                            <ValidatingTextArea
                                className="form-control"
                                placeholder="Additional protocol information"
                                value={offer.protocolAdditionalInfo}
                                onChange={(e) => mergeData({ protocolAdditionalInfo: e.target.value })}
                            />
                        </label>
                    </div>

                    <div className={theme.pagePadding}>
                        <h4>Preview</h4>
                        <div className={theme.offer} style={{ borderLeft: `5px solid ${offer.color}` }}>
                            <div className={theme.offerDetails}>
                                <div className={theme.logo}>
                                    <Thumbnail
                                        src={offer.logoFile}
                                        alt={`Rubus Placeholder Logo`}
                                        className={theme.logoImage}
                                    />
                                </div>
                                <div className={theme.section}>
                                    <div className={theme.header}>{offer.highlightedTitle1}</div>
                                    <div className={theme.subHeader}>{offer.highlightedSubTitle1}</div>
                                </div>
                                <div className={theme.section}>
                                    <div className={theme.header}>{offer.highlightedTitle2}</div>
                                    <div className={theme.subHeader}>{offer.highlightedSubTitle2}</div>
                                </div>
                                <div className={theme.section}>
                                    <div className={theme.header}>{offer.highlightedTitle3}</div>
                                    <div className={theme.subHeader}>{offer.highlightedSubTitle3}</div>
                                </div>
                            </div>
                            <div className={theme.terms}>
                                <div className={theme.termsHeader}>Confirmation</div>
                                <div className={theme.termsContent}>
                                    <Checkbox checked={isChecked} onChange={() => setIsChecked(!isChecked)}>
                                        <div>{offer.terms}</div>
                                    </Checkbox>
                                </div>

                                <div className={theme.termLinks}>
                                    {offer.termSheetLink1 && (
                                        <a href={offer.termSheetLink1} target="_blank" rel="noopener noreferrer">
                                            Terms of Agreement
                                        </a>
                                    )}
                                    {offer.termSheetLink2 && (
                                        <a href={offer.termSheetLink2} target="_blank" rel="noopener noreferrer">
                                            Cancellation Form
                                        </a>
                                    )}
                                    {offer.termSheetLink3 && (
                                        <a href={offer.termSheetLink3} target="_blank" rel="noopener noreferrer">
                                            Cancellation Policy
                                        </a>
                                    )}
                                    {offer.priceListLink && (
                                        <a href={offer.priceListLink} target="_blank" rel="noopener noreferrer">
                                            Pricing List
                                        </a>
                                    )}
                                </div>
                            </div>
                            <div className={theme.button}>
                                <button
                                    className={theme.selectButton}
                                    style={{ backgroundColor: `${offer.color}` }}
                                    onClick={() => {}}>
                                    Select contract
                                    <span>
                                        <FontAwesomeIcon icon={faArrowRight} />
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={theme.row}>
                    <Button loading={isSaving} type="submit" color="primary">
                        Save
                    </Button>
                </div>
            </ValidationForm>
        </div>
    );
};

export default ElectricitySupplierDetails;
